import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { newsArticles } from '@/mocks/newsData';

const CAT_KEYS = [
  { value: 'all', labelKey: 'news.list.allLabel' },
  { value: 'schedule', labelKey: 'news.list.catSchedule' },
  { value: 'seminar', labelKey: 'news.list.catSeminar' },
  { value: 'update', labelKey: 'news.list.catUpdate' },
  { value: 'other', labelKey: 'news.list.catOther' },
];

const NEWS_PAGE_SIZE = 10;

export default function NewsList() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(NEWS_PAGE_SIZE);

  const filteredArticles = activeCategory === 'all'
    ? newsArticles
    : newsArticles.filter((a) => a.category === activeCategory);
  const visibleArticles = filteredArticles.slice(0, visibleCount);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setVisibleCount(NEWS_PAGE_SIZE);
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            NEWS LIST
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('news.hero.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('news.hero.titleEn')}
          </p>
          <div className="w-10 h-[1px] bg-accent-500/40 mx-auto mb-6"></div>
          <p className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[720px] mx-auto">
            {t('news.hero.lead')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {CAT_KEYS.map((cat) => (
            <button
              key={cat.value}
              onClick={() => handleCategoryChange(cat.value)}
              className={`px-4 py-2 text-[14px] tracking-[0.05em] whitespace-nowrap cursor-pointer transition-all duration-300 ${
                activeCategory === cat.value
                  ? 'bg-accent-500 text-primary-900'
                  : 'border border-background-200/60 text-foreground-500 hover:text-accent-500 hover:border-accent-400/40'
              }`}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>

        {/* Articles — 広幅1行レイアウト(§17): 日付/カテゴリ左寄せ固定幅 + タイトル1行 + 矢印右端 */}
        {filteredArticles.length > 0 ? (
          <>
            <div className="space-y-0 max-w-[1180px] mx-auto">
              {visibleArticles.map((article, idx) => (
                <Link
                  key={idx}
                  to={`/news/${article.slug}`}
                  className={`card-glow group flex flex-col md:flex-row md:items-center gap-8 px-2 md:px-4 py-5 border-b border-background-200/60 hover:bg-background-100/80 transition-all duration-300 cursor-pointer ${
                    idx === 0 ? 'border-t' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 md:contents">
                  <span className="md:w-[12em] flex-shrink-0 text-[14px] text-foreground-400 tracking-[0.04em] whitespace-nowrap tabular-nums">
                    {article.date}
                  </span>
                  <span className="md:w-[8em] flex-shrink-0">
                    <span className="inline-block text-[12px] px-2 py-0.5 border border-accent-400/30 text-accent-500 whitespace-nowrap tracking-[0.06em]">
                      {article.categoryLabel}
                    </span>
                  </span>
                  </div>
                  <h3 className="w-full md:flex-1 min-w-0 text-[16px] text-foreground-800 tracking-[0.04em] font-medium group-hover:text-accent-600 transition-colors leading-relaxed md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
                    {article.title}
                  </h3>
                  <div className="hidden sm:flex items-center flex-shrink-0 w-5 h-5">
                    <i className="ri-arrow-right-s-line text-accent-400 text-base opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                  </div>
                </Link>
              ))}
            </div>
            {visibleCount < filteredArticles.length && (
              <div className="mt-10 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + NEWS_PAGE_SIZE)}
                  className="btn-glow inline-flex items-center gap-3 px-8 py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap"
                >
                  続きを表示
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-[15px] text-foreground-400 tracking-[0.04em] py-12">
            {t('news.list.empty')}
          </p>
        )}

      </div>
    </section>
  );
}
